import { createAppStore } from '@app/core/stores';
import { nlpIntegration } from '@mi-front/api-clients';
import merge from 'lodash/merge';
import { DeepPartial } from 'redux';
import { StreamMode } from '../types';

export enum AutoOpenFragmentMode {
  NEVER = 'NEVER',
  ON_START_OR_END = 'ON_START_OR_END',
  ON_START_AND_END = 'ON_START_AND_END',
}

export type StreamDrawerContentType =
  | 'live-topics'
  | 'create-fragment'
  | 'create-joint-fragment'
  | 'live-topics-standalone'
  | null;

/** Payload for the stream drawer. Not persisted. */
export type StreamDrawerData = { topic: nlpIntegration.TopicOutDto } | null;

interface StreamSettingsState {
  /** Drawer is open when drawerType is non-null. */
  drawerType: StreamDrawerContentType | null;
  drawerData: StreamDrawerData;
  audit: {
    matches: boolean;
    liveMentions: boolean;
    liveTopics: boolean;
    /** Personas track / tab visibility toggle. */
    persons: boolean;
    /**
     * Personas track density. Optional for persisted v1 snapshots that lack the key;
     * readers must default to `'summary'`.
     */
    personsDensity?: 'summary' | 'lanes';
    lowerthird: boolean;
    adoverlay: boolean;
    screenbug: boolean;
    ytstats: boolean;
  };
  mode: StreamMode;
  autoOpenFragmentMode: AutoOpenFragmentMode;

  // Controls whether playback should start automatically when clicking on focus
  playOnCursorSet: boolean;

  // Controls whether the selection should be kept when the range changes
  keepSelectionOnRangeChange: boolean;

  /** True while a selection VIDEO export job is being prepared (used by menu and hotkey). Not persisted. */
  isPreparingSelectionVideoDownload: boolean;
  setPreparingSelectionVideoDownload: (value: boolean) => void;
  /** True while a selection AUDIO export job is being prepared (used by menu and hotkey). Not persisted. */
  isPreparingSelectionAudioDownload: boolean;
  setPreparingSelectionAudioDownload: (value: boolean) => void;

  // Actions
  updateSettings: (data: DeepPartial<StreamSettingsState>) => void;
}

/**
 * Persisted slice; preparing selection download flags/setters are ephemeral and not persisted.
 */
type PersistedStreamSettingsState = Omit<
  StreamSettingsState,
  | 'isPreparingSelectionVideoDownload'
  | 'setPreparingSelectionVideoDownload'
  | 'isPreparingSelectionAudioDownload'
  | 'setPreparingSelectionAudioDownload'
>;

/**
 * Zustand store for managing stream screen settings.
 * Replaces the Redux implementation with a simpler Zustand store.
 * Persisted and registered for cleanup on logout.
 * Preparing selection download flags/setters are not persisted (ephemeral UI state).
 */
export const useStreamSettingsStore = createAppStore<
  StreamSettingsState,
  PersistedStreamSettingsState
>('StreamSettingsStore', {
  persist: {
    name: 'app.streamSettings',
    version: 2,
    migrate: (persistedState, version) => {
      if (!persistedState || typeof persistedState !== 'object') {
        return persistedState as PersistedStreamSettingsState;
      }
      const state = persistedState as PersistedStreamSettingsState & {
        audit?: PersistedStreamSettingsState['audit'] & { diarization?: boolean };
      };
      if (version < 2 && state.audit) {
        const { diarization, ...restAudit } = state.audit;
        return {
          ...state,
          audit: {
            ...restAudit,
            persons: diarization ?? state.audit.persons ?? true,
          },
        };
      }
      return state;
    },
    partialize: (state): PersistedStreamSettingsState => {
      const {
        isPreparingSelectionVideoDownload: _videoPreparing,
        setPreparingSelectionVideoDownload: _setVideoPreparing,
        isPreparingSelectionAudioDownload: _audioPreparing,
        setPreparingSelectionAudioDownload: _setAudioPreparing,
        ...rest
      } = state;
      return rest;
    },
    merge: (persisted, current) => ({ ...current, ...(persisted ?? {}) }),
  },
})((set) => ({
  audit: {
    matches: true,
    liveMentions: true,
    liveTopics: true,
    persons: true,
    personsDensity: 'summary',
    lowerthird: false,
    adoverlay: false,
    screenbug: false,
    ytstats: false,
  },
  mode: StreamMode.DEFAULT,
  autoOpenFragmentMode: AutoOpenFragmentMode.NEVER,
  playOnCursorSet: true,
  keepSelectionOnRangeChange: false,

  drawerType: null,
  drawerData: null,

  isPreparingSelectionVideoDownload: false,
  setPreparingSelectionVideoDownload: (value) => set({ isPreparingSelectionVideoDownload: value }),
  isPreparingSelectionAudioDownload: false,
  setPreparingSelectionAudioDownload: (value) => set({ isPreparingSelectionAudioDownload: value }),

  // Update settings with partial data (deep merges with existing state)
  updateSettings: (data) => {
    set((state) => merge({}, state, data));
  },
}));
